import { useEffect, useState } from 'react';
import { listChildren } from '@/features/children/api';

/**
 * Module-level cached child-profile count — drives the AccountRoute
 * "Kinderprofile (count)" badge alongside `useStoryCount` for the
 * Bibliothek badge (Batch 8 Commit 5 wiring).
 *
 * Cache + inflight-promise dedupe pattern mirrors `useEntitlement`,
 * `useSubscriptionState`, `useUnreadNotificationCount`, `useStoryCount`.
 *
 * The backend has no dedicated `/children/count` endpoint — Phase 1E's
 * `GET /api/children` returns the full owner-scoped list. Reading
 * `.length` is the cheapest path; Mai-Launch caps via
 * `MAX_CHILDREN_PER_ACCOUNT` (default 10) keep the payload tiny.
 *
 * Invalidation: callers that mutate the user's children set
 * (ChildProfileRoute create/update, ChildrenRoute delete) MUST call
 * `clearChildCountCache()` after the mutation resolves so the next
 * AccountRoute mount sees the updated count.
 */

let cachedCount: number | null = null;
let inflight: Promise<number> | null = null;

export function clearChildCountCache(): void {
  cachedCount = null;
  inflight = null;
}

async function fetchCount(): Promise<number> {
  if (inflight) return inflight;
  inflight = listChildren()
    .then((list) => {
      cachedCount = list.length;
      return list.length;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export interface UseChildCountResult {
  count: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useChildCount(): UseChildCountResult {
  const [count, setCount] = useState<number>(cachedCount ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(cachedCount === null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchCount()
      .then((next) => {
        if (cancelled) return;
        setCount(next);
        setIsLoading(false);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setIsLoading(false);
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const next = await fetchCount();
      setCount(next);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  return { count, isLoading, error, refetch };
}
